import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { PageHeader } from "../components/PageHeader";
import { Toolbar, ToolbarGroup } from "../components/Toolbar";
import { currentUtcMonth } from "../lib/readingsApi";
import {
  runAwitatpapuriScrape,
  runUsccbScrape,
  SCRAPE_DAYS_PER_INVOKE,
  type ScrapeResult,
} from "../lib/scrapeApi";

function monthBounds(month: string): { start: string; end: string } {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
  const mm = String(m).padStart(2, "0");
  return {
    start: `${y}-${mm}-01`,
    end: `${y}-${mm}-${String(last).padStart(2, "0")}`,
  };
}

type SourceKey = "usccb" | "awitatpapuri" | "both";

export function ScrapePage() {
  const defaults = useMemo(() => monthBounds(currentUtcMonth()), []);
  const [dateKeyStart, setDateKeyStart] = useState(defaults.start);
  const [dateKeyEnd, setDateKeyEnd] = useState(defaults.end);
  const [busy, setBusy] = useState<SourceKey | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ScrapeResult | null>(null);
  const [bothNotes, setBothNotes] = useState<string | null>(null);

  async function onRun(source: SourceKey) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKeyStart) || !/^\d{4}-\d{2}-\d{2}$/.test(dateKeyEnd)) {
      setError("Pick valid start and end dates (YYYY-MM-DD).");
      return;
    }
    setBusy(source);
    setError(null);
    setResult(null);
    setBothNotes(null);
    setProgress(null);
    try {
      const onProgress = (p: {
        chunkIndex: number;
        chunkTotal: number;
        dateKeyStart: string;
        dateKeyEnd: string;
      }) => {
        setProgress(`Chunk ${p.chunkIndex}/${p.chunkTotal} · ${p.dateKeyStart} → ${p.dateKeyEnd}`);
      };

      if (source === "both") {
        const en = await runUsccbScrape({ dateKeyStart, dateKeyEnd }, onProgress);
        const tl = await runAwitatpapuriScrape({ dateKeyStart, dateKeyEnd }, onProgress);
        setBothNotes(
          `EN ${en.packsCreated} drafted / ${en.daysFailed} failed · TL ${tl.packsCreated} drafted / ${tl.daysFailed} failed`,
        );
        setResult(tl);
      } else {
        const data =
          source === "usccb"
            ? await runUsccbScrape({ dateKeyStart, dateKeyEnd }, onProgress)
            : await runAwitatpapuriScrape({ dateKeyStart, dateKeyEnd }, onProgress);
        setResult(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Scrape failed.");
    } finally {
      setBusy(null);
      setProgress(null);
    }
  }

  return (
    <AppShell>
      <PageHeader
        eyebrow="Readings"
        title="Scrape"
        description={
          <>
            Pull USCCB (EN) or Awit at Papuri (TL) into CloudKit as <strong>draft</strong> packs.
            Published packs are never overwritten. Runs in chunks of{" "}
            <strong>{SCRAPE_DAYS_PER_INVOKE}</strong> days.
          </>
        }
        actions={
          <Toolbar label="Run scrape">
            <ToolbarGroup>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy != null || !dateKeyStart || !dateKeyEnd}
                onClick={() => void onRun("both")}
              >
                {busy === "both" ? "Scraping…" : "EN + TL"}
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                disabled={busy != null || !dateKeyStart || !dateKeyEnd}
                onClick={() => void onRun("usccb")}
              >
                {busy === "usccb" ? "…" : "EN only"}
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                disabled={busy != null || !dateKeyStart || !dateKeyEnd}
                onClick={() => void onRun("awitatpapuri")}
              >
                {busy === "awitatpapuri" ? "…" : "TL only"}
              </button>
            </ToolbarGroup>
            <Link className="btn btn-quiet btn-sm" to="/readings">
              Calendar
            </Link>
          </Toolbar>
        }
      />

      <div className="panel panel-pad stack" style={{ marginBottom: 16 }}>
        <div className="section-title">
          <h2>Date range</h2>
          <p>
            USCCB → <code>en</code> · Awit at Papuri → <code>tl</code>. Tagalog prefers the latest
            Awit revision URL when available.
          </p>
        </div>

        <div className="scrape-fields">
          <label className="field">
            <span>Start date</span>
            <input
              type="date"
              value={dateKeyStart}
              onChange={(e) => setDateKeyStart(e.target.value)}
              disabled={busy != null}
            />
          </label>
          <label className="field">
            <span>End date</span>
            <input
              type="date"
              value={dateKeyEnd}
              onChange={(e) => setDateKeyEnd(e.target.value)}
              disabled={busy != null}
            />
          </label>
        </div>
      </div>

      {progress && (
        <p className="muted" style={{ margin: "0 0 16px", fontSize: 13 }}>
          {progress}
        </p>
      )}

      {error && (
        <div className="error-banner" role="alert" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}

      {bothNotes && (
        <div className="banner-info" role="status" style={{ marginBottom: 12 }}>
          {bothNotes}
        </div>
      )}

      {result && (
        <div className="panel panel-pad stack">
          <div className="section-title">
            <h2>Result</h2>
            <p>
              Job <code>{result.jobId}</code> · {result.source}/{result.languageCode} ·{" "}
              {result.status}
              {result.chunks != null && result.chunks > 1 ? ` · ${result.chunks} chunks` : ""}
            </p>
          </div>

          <div className="grid-stats" style={{ marginBottom: 0 }}>
            <div className="panel stat">
              <div className="label">Drafts written</div>
              <div className="value">{result.packsCreated}</div>
            </div>
            <div className="panel stat">
              <div className="label">Skipped published</div>
              <div className="value">{result.packsSkippedPublished}</div>
            </div>
            <div className="panel stat">
              <div className="label">Failed days</div>
              <div className="value">{result.daysFailed}</div>
            </div>
            <div className="panel stat">
              <div className="label">Attempted</div>
              <div className="value">{result.daysAttempted}</div>
            </div>
          </div>

          {result.errorSummary && (
            <div className="error-banner" role="status">
              {result.errorSummary}
            </div>
          )}

          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {result.results.map((row) => (
                  <tr key={row.dateKey}>
                    <td>
                      <Link className="link-inline" to={`/readings/${row.dateKey}`}>
                        {row.dateKey}
                      </Link>
                    </td>
                    <td>
                      {row.skippedPublished ? (
                        <span className="chip">published skipped</span>
                      ) : row.ok ? (
                        <span className="chip">ok</span>
                      ) : (
                        <span className="chip neutral">failed</span>
                      )}
                    </td>
                    <td className="muted" style={{ fontSize: 13 }}>
                      {row.error ??
                        (row.skippedPublished
                          ? "Existing published pack left untouched"
                          : `${row.title ?? "Draft saved"}${
                              row.sectionCount != null ? ` · ${row.sectionCount} sections` : ""
                            }${row.color ? ` · ${row.color}` : ""}`)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AppShell>
  );
}
